import React, { useCallback, useEffect, useState } from "react";
import { Search, X, Plus, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { qrtListUsers, qrtCreateUser, qrtUpdateUser } from "../../apis/qrtAdmin";
import { Pager, Spinner, StatusChip, fmtDateTime, fmtMoney, num } from "./qrtUi";

const PAGE_SIZE = 25;

const UserForm = ({ initial, onClose, onSaved }) => {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({
    name: initial?.name || "",
    email: initial?.email || "",
    phone: initial?.phone || "",
    password: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isEdit) {
        const payload = { name: form.name, email: form.email, phone: form.phone };
        if (form.password) payload.new_password = form.password;
        await qrtUpdateUser(initial.id, payload);
        toast.success("User updated");
      } else {
        await qrtCreateUser(form);
        toast.success("User created");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="ping-drawer-backdrop" onClick={onClose} />
      <aside className="ping-drawer" role="dialog" aria-modal="true">
        <form className="qrt-form" onSubmit={submit} style={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <div className="ping-drawer-head">
            <div>
              <h3>{isEdit ? "Edit user" : "New user"}</h3>
              <p>{isEdit ? `Account #${initial.id}` : "Creates an account owner"}</p>
            </div>
            <button type="button" className="camp-ico" onClick={onClose} aria-label="Close">
              <X size={16} />
            </button>
          </div>
          <div className="ping-drawer-body qrt-form">
            <label>
              Name
              <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
            </label>
            <label>
              Email
              <input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} required />
            </label>
            <label>
              Phone
              <input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
            </label>
            <label>
              {isEdit ? "Reset password (leave blank to keep)" : "Password (min 8 chars)"}
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                minLength={form.password ? 8 : undefined}
                placeholder={isEdit ? "Unchanged" : ""}
              />
            </label>
          </div>
          <div className="ping-drawer-foot">
            <button type="button" className="ping-btn ping-btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="ping-btn ping-btn-green" disabled={busy}>
              {busy && <Loader2 size={14} className="animate-spin" />}
              {isEdit ? "Save changes" : "Create user"}
            </button>
          </div>
        </form>
      </aside>
    </>
  );
};

const QrtUsers = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ rows: [], total: 0 });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [partner, setPartner] = useState("");
  const [partners, setPartners] = useState([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await qrtListUsers({
        search, limit: PAGE_SIZE, offset: page * PAGE_SIZE,
        ...(status ? { status } : {}),
        ...(partner ? { partner } : {}),
      });
      const payload = res.data?.data || { rows: [], total: 0 };
      setData(payload);
      if (Array.isArray(payload.partners)) setPartners(payload.partners);
    } catch (_) {}
    finally { setLoading(false); }
  }, [search, status, partner, page]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div>
      <div className="qrt-page-head">
        <div>
          <h2>Users</h2>
          <p>{num(data.total)} account owners · open a row for campaigns, revenue and controls</p>
        </div>
        <div className="grow" />
        <button type="button" className="ping-btn ping-btn-green ping-btn-sm" onClick={() => setEditing({})}>
          <Plus size={15} /> New user
        </button>
      </div>

      <div className="qrt-card">
        <div className="qrt-card-head">
          <Search size={15} style={{ color: "var(--text-3)" }} />
          <input
            type="search"
            placeholder="Search name / email / phone…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            style={{ flex: 1, border: "none", outline: "none" }}
          />
          <select value={partner} onChange={(e) => { setPartner(e.target.value); setPage(0); }}>
            <option value="">All workspaces</option>
            <option value="direct">Blutec Ping (direct)</option>
            {partners.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }}>
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Deactivated</option>
          </select>
        </div>

        {loading ? <Spinner /> : (
          <table className="qrt-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Workspace</th>
                <th>Plan</th>
                <th className="r">Balance</th>
                <th className="r">Spend</th>
                <th className="r">Campaigns</th>
                <th className="r">Flows</th>
                <th className="r">Msgs sent</th>
                <th>Status</th>
                <th>Wallet</th>
                <th className="r">Joined</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((u) => (
                <tr key={u.id} className="click" onClick={() => navigate(`/qrt/users/${u.id}`)}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{u.name}</div>
                    <div className="sub">{u.email}</div>
                  </td>
                  <td>
                    {u.partner_id ? (
                      <span className="ping-chip p" title={u.app_domain || ""}>{u.partner_name || `Partner #${u.partner_id}`}</span>
                    ) : (
                      <span className="ping-chip n">Blutec Ping</span>
                    )}
                  </td>
                  <td>
                    {u.plan_name ? (
                      <>
                        <div style={{ fontWeight: 600 }}>{u.plan_name}</div>
                        {u.plan_expires_at && (
                          <div className="sub">until {fmtDateTime(u.plan_expires_at)}</div>
                        )}
                      </>
                    ) : (
                      <span className="sub">No plan</span>
                    )}
                  </td>
                  <td className="r" style={{ fontWeight: 700 }}>{fmtMoney(u.balance)}</td>
                  <td className="r" title={u.partner_id ? "Settles to the partner, not Blutec" : "Blutec revenue"}>
                    {fmtMoney(u.spend ?? u.revenue)}
                  </td>
                  <td className="r">{num(u.campaign_count)}</td>
                  <td className="r">{num(u.flow_count)}</td>
                  <td className="r">{num(u.messages_sent)}</td>
                  <td><StatusChip value={u.status || "active"} /></td>
                  <td><StatusChip value={u.wallet_status} /></td>
                  <td className="r sub">{fmtDateTime(u.created_at)}</td>
                </tr>
              ))}
              {data.rows.length === 0 && (
                <tr><td className="empty" colSpan={11}>No users found</td></tr>
              )}
            </tbody>
          </table>
        )}
        <Pager page={page} total={data.total} pageSize={PAGE_SIZE} onPage={setPage} />
      </div>

      {editing && (
        <UserForm initial={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={load} />
      )}
    </div>
  );
};

export default QrtUsers;
export { UserForm };
